// Google serves a gray "no photo" silhouette for accounts without a profile
// picture. Unlike a broken URL it loads with HTTP 200, so an <img> `onError`
// never fires and we would otherwise show a useless gray blob instead of
// initials. We can only read the pixels of images that allow cross-origin
// reads; Google's avatar CDN does, so we scope both the check and the
// crossOrigin attribute to it.
export const isInspectableAvatar = (avatar?: string | null): avatar is string =>
  !!avatar && avatar.includes("googleusercontent.com");

// A real photo (or Google's colored letter-avatar) always has some color; the
// placeholder is fully grayscale. `data` is RGBA pixels from a canvas.
const GRAYSCALE_CHANNEL_TOLERANCE = 12;

export const pixelsAreGrayscale = (data: Uint8ClampedArray): boolean => {
  if (data.length === 0) {
    return false;
  }

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    if (
      Math.abs(r - g) > GRAYSCALE_CHANNEL_TOLERANCE ||
      Math.abs(g - b) > GRAYSCALE_CHANNEL_TOLERANCE ||
      Math.abs(r - b) > GRAYSCALE_CHANNEL_TOLERANCE
    ) {
      return false;
    }
  }

  return true;
};

export const avatarImageIsGrayscalePlaceholder = (
  img: HTMLImageElement,
): boolean => {
  try {
    const sampleSize = 16;
    const canvas = document.createElement("canvas");
    canvas.width = sampleSize;
    canvas.height = sampleSize;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      return false;
    }

    ctx.drawImage(img, 0, 0, sampleSize, sampleSize);
    return pixelsAreGrayscale(
      ctx.getImageData(0, 0, sampleSize, sampleSize).data,
    );
  } catch {
    // Reading pixels can fail (e.g. a tainted canvas). Fail open: keep the image.
    return false;
  }
};
